import React, { useEffect, useState } from "react";
import {
  Box,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  Select,
  Text,
} from "native-base";
import Input from "../../../../../components/Input";
import InputMask from "../../../../../components/InputCustom";
import { useCEPSearch } from "../hooks/useCEPSearch";
import SelectWeb from "../../../../../components/SelectWeb";
import Loader from "../../../../../components/Loader";
import { useTheme } from "../../../../../ThemeProvider";

interface DadosComplementaresProps {
  getConfigEmpresa: any;
  cep: string;
  setCep: (value: string) => void;
  endereco: string;
  setEndereco: (value: string) => void;
  numero: string;
  setNumero: (value: string) => void;
  bairro: string;
  setBairro: (value: string) => void;
  cidade: string;
  setCidade: (value: string) => void;
  estado: string;
  setEstado: (value: string) => void;
}

const DadosComplementares: React.FC<DadosComplementaresProps> = ({
  getConfigEmpresa,
  cep,
  setCep,
  endereco,
  setEndereco,
  numero,
  setNumero,
  bairro,
  setBairro,
  cidade,
  setCidade,
  estado,
  setEstado,
}) => {
  const {
    isLoading,
    error,
    buscarCEP,
    limparErro,
    buscarEstados,
    buscarCidades,
  } = useCEPSearch();
  const [cepError, setCepError] = useState<string | null>(null);
  const [isLoadingDados, setIsLoadingDados] = useState<boolean>(false);
  const [listEstados, setListEstados] = useState<any[]>([]);
  const [listCidades, setListCidades] = useState<any[]>([]);
  const [cidadeCompleted, setCidadeCompleted] = useState<string>("");
  const { primaryColor } = useTheme();
  const corFoco = primaryColor || undefined;

  const handleCepBlur = async () => {
    if (!cep.trim()) {
      return; // Não faz nada se CEP estiver vazio
    }

    limparErro();
    setCepError(null);
    setIsLoadingDados(true);
    const resultado = await buscarCEP(cep);

    if (resultado) {
      // Preenche automaticamente os campos
      setEndereco(resultado.logradouro);
      setBairro(resultado.bairro);

      const estado = listEstados.find((item) => item.uf === resultado.uf);

      if (estado) {
        setEstado(estado.value);
      }

      setCidadeCompleted(resultado.localidade);
    }
    setIsLoadingDados(false);
  };

  const handleCepChange = (value: string) => {
    setCep(value);
    // Limpa erro quando usuário começa a digitar
    if (cepError) {
      setCepError(null);
      setIsLoadingDados(false);
    }
    if (error) {
      limparErro();
      setIsLoadingDados(false);
    }
  };

  useEffect(() => {
    buscarEstados().then((res) => {
      setListEstados(res || []);
    });
  }, []);

  useEffect(() => {
    if (estado) {
      buscarCidades(Number(estado)).then((res) => {
        const cidade = res?.find((item) => item.label === cidadeCompleted);
        if (cidade) {
          setCidade(cidade.value);
        }
        setListCidades(res || []);
      });
    }
  }, [estado]);

  useEffect(() => {
    if (listEstados.length > 0) {
      setEstado(estado);
    }
  }, [listEstados]);

  return (
    <>
      <Box
        style={{ width: "100%" }}
        overflow="visible"
        zIndex={1}
        position="relative"
      >
        <Input
          colorItem={getConfigEmpresa.coresPWA.corFonteFiltro}
          corBordaFiltro={getConfigEmpresa.coresPWA.corBordaFiltro}
          value={cep}
          styleBox={{ marginTop: 4 }}
          type="number"
          onChangeText={handleCepChange}
          onBlur={handleCepBlur}
          width={"100%"}
          placeholder="00000-000"
          helperText={isLoading ? "Buscando endereço..." : undefined}
          bg={"#fff"}
          estiloNovo={{
            rotulo: "CEP",
            corFoco,
            erro: !!(cepError || error),
          }}
        />

        {(cepError || error) && (
          <Text color="#C0392B" fontSize={12} marginTop={1.5}>
            {cepError || error}
          </Text>
        )}
        <Input
          colorItem={getConfigEmpresa.coresPWA.corFonteFiltro}
          corBordaFiltro={getConfigEmpresa.coresPWA.corBordaFiltro}
          width={"100%"}
          styleBox={{ marginTop: 14 }}
          placeholder={"Rua, avenida..."}
          value={endereco}
          bg={"#fff"}
          onChangeText={(text) => {
            setEndereco(text);
          }}
          estiloNovo={{ rotulo: "Endereço", corFoco }}
        />

        <Box style={{ flexDirection: "row", gap: 10, marginTop: 14 }}>
          <Box style={{ width: "34%" }}>
            <Input
              colorItem={getConfigEmpresa.coresPWA.corFonteFiltro}
              corBordaFiltro={getConfigEmpresa.coresPWA.corBordaFiltro}
              value={numero}
              type="number"
              onChangeText={(text) => {
                if (!/^\d*$/.test(text)) {
                  return;
                }
                setNumero(text);
              }}
              width={"100%"}
              placeholder="Nº"
              bg={"#fff"}
              estiloNovo={{ rotulo: "Número", corFoco }}
            />
          </Box>
          <Box style={{ flex: 1 }}>
            <Input
              colorItem={getConfigEmpresa.coresPWA.corFonteFiltro}
              corBordaFiltro={getConfigEmpresa.coresPWA.corBordaFiltro}
              width={"100%"}
              placeholder={"Bairro"}
              value={bairro}
              onChangeText={(text) => {
                setBairro(text);
              }}
              bg={"#fff"}
              estiloNovo={{ rotulo: "Bairro", corFoco }}
            />
          </Box>
        </Box>

        <Box
          style={{ flexDirection: "row", gap: 10, marginTop: 14 }}
          position="relative"
          overflow="visible"
          zIndex={2}
        >
          <Box
            style={{ width: "34%" }}
            position="relative"
            overflow="visible"
            zIndex={2}
          >
            <SelectWeb
              items={listEstados}
              placeholder="UF"
              value={estado}
              onChange={(val: any) => setEstado(val)}
              getConfigEmpresa={getConfigEmpresa}
              estiloNovo={{ rotulo: "Estado", corFoco }}
            />
          </Box>

          <Box
            style={{ flex: 1 }}
            position="relative"
            overflow="visible"
            zIndex={1}
          >
            <SelectWeb
              items={listCidades}
              placeholder="Selecione"
              value={cidade}
              onChange={(val: any) => setCidade(val)}
              getConfigEmpresa={getConfigEmpresa}
              estiloNovo={{ rotulo: "Cidade", corFoco }}
            />
          </Box>
        </Box>
      </Box>

      {isLoadingDados && <Loader />}
    </>
  );
};

export default DadosComplementares;
